"use client";

import { type StorefrontUrls } from "@/app/utilities/storefront-urls";
import { Breadcrumbs } from "@/composites/navigation";
import {
	PlpFacetRail,
	PlpHero,
	PlpPagination,
	PlpPopularFilters,
	PlpProductGrid,
	PlpResultsToolbar
} from ".";
import { type Locale } from "@/domain/catalog";
import { type SuperCategory } from "@/domain/product-listing";
import {
	AlertBanner,
	Col as DSCol,
	Container,
	Grid,
	type ColProps
} from "@pamperedchef/design-system";
import * as styles from "./ProductListingTemplate.module.scss";
import { useEffect, type FC, type ReactNode } from "react";
import { useAddToCartErrorStore } from "@/app/store";
import { buildPlpBreadcrumbItems } from "./helpers/build-plp-breadcrumb-items";
import {
	ProductListingResults,
	type ProductListingResultsProps,
	type ProductListingSearchCredentials
} from "./ProductListingResults";

const Col = DSCol as FC<ColProps & { children?: ReactNode }>;

// Relative-href default for stories / tests that don't pass `urls`. Real
// storefront URLs are resolved server-side via `getStorefrontUrls()` in the
// route and threaded down — never resolved inside this component.
const DEFAULT_HOME_URL = "/";

export interface ProductListingTemplateProps {
	category: SuperCategory;
	locale: Locale;
	search: ProductListingSearchCredentials;
	urls?: Pick<StorefrontUrls, "home">;
	searchClient?: ProductListingResultsProps["searchClient"];
}

export function ProductListingTemplate({
	category,
	locale,
	search,
	urls,
	searchClient
}: ProductListingTemplateProps): JSX.Element {
	const errorMessage = useAddToCartErrorStore((state) => state.message);
	const errorSeq = useAddToCartErrorStore((state) => state.seq);
	const clearError = useAddToCartErrorStore((state) => state.clear);

	useEffect(() => {
		clearError();
	}, [clearError]);

	useEffect(() => {
		if (errorMessage) window.scrollTo({ top: 0, behavior: "smooth" });
	}, [errorMessage, errorSeq]);

	return (
		<article
			data-template="ProductListingTemplate"
			data-category={category.slug}
		>
			{errorMessage ? (
				<AlertBanner
					kind="error"
					content={errorMessage}
					cta={{ link: "", title: "" }}
					className={styles["atcErrorRibbon"]}
				/>
			) : null}

			<Container>
				<Grid columns={12} gap="lg">
					<Col size={12}>
						<Breadcrumbs
							items={buildPlpBreadcrumbItems(category, {
								homeUrl: urls?.home ?? DEFAULT_HOME_URL
							})}
						/>
					</Col>

					<Col size={12}>
						<PlpHero title={category.name} />
					</Col>

					<Col size={12}>
						<PlpPopularFilters />
					</Col>

					<ProductListingResults
						category={category}
						locale={locale}
						search={search}
						searchClient={searchClient}
					>
						<Col size={12} md={3}>
							<PlpFacetRail />
						</Col>

						<Col size={12} md={9}>
							<PlpResultsToolbar locale={locale} />
							<PlpProductGrid locale={locale} />
							<PlpPagination />
						</Col>
					</ProductListingResults>
				</Grid>
			</Container>
		</article>
	);
}
